import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  ChevronRight,
  ChevronDown,
  FileText,
  Folder,
  Link as LinkIcon,
  Plus,
  Settings,
  Trash2,
} from "lucide-react";
import customFetch from "../../../helpers/axios";
import { normalizeApiResponse } from "../../../helpers/apiUtils";
import useUser from "../../../hooks/useUser";
import Button from "../../components/Buttons/Button/Button";
import SearchInput from "../../components/SearchInput/SearchInput";
import "./KnowledgeBases.css";

const ROOT_KEY = "root";
const POLL_INTERVAL_MS = 10000;

// ------------------------------------------------------------------ //
// Sync status badge                                                    //
// ------------------------------------------------------------------ //

const STATUS_LABELS = {
  pending: "Pending",
  syncing: "Syncing…",
  synced: "Synced",
  failed: "Failed",
};

function SyncBadge({ status }) {
  if (!status) return null;
  return (
    <span className={`kb-sync-badge kb-sync-badge--${status}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ------------------------------------------------------------------ //
// HierarchyTree                                                        //
// ------------------------------------------------------------------ //

function HierarchyTree({
  folders,
  items,
  currentFolderId,
  rootLabel,
  searchTerm,
  onSelectFolder,
  onSelectItem,
  getItemId,
  getItemLabel,
  getItemSearchText,
  renderItemIcon,
  renderFolderActions,
  renderItemActions,
}) {
  const [expandedIds, setExpandedIds] = useState(() => new Set([ROOT_KEY]));
  const folderMap = useMemo(
    () => Object.fromEntries(folders.map((folder) => [String(folder.id), folder])),
    [folders]
  );
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  useEffect(() => {
    const nextExpandedIds = new Set([ROOT_KEY]);
    let pointer = currentFolderId === ROOT_KEY ? null : folderMap[String(currentFolderId)];

    while (pointer) {
      nextExpandedIds.add(String(pointer.id));
      const parentId = pointer.parent ? String(pointer.parent) : ROOT_KEY;
      nextExpandedIds.add(parentId);
      pointer = parentId !== ROOT_KEY ? folderMap[parentId] : null;
    }

    setExpandedIds((previous) => new Set([...previous, ...nextExpandedIds]));
  }, [currentFolderId, folderMap]);

  const childrenByParent = useMemo(() => {
    return folders.reduce((accumulator, folder) => {
      const parentKey = folder.parent ? String(folder.parent) : ROOT_KEY;
      if (!accumulator[parentKey]) accumulator[parentKey] = [];
      accumulator[parentKey].push(folder);
      return accumulator;
    }, {});
  }, [folders]);

  const itemsByParent = useMemo(() => {
    return items.reduce((accumulator, item) => {
      const parentKey = item.folder ? String(item.folder) : ROOT_KEY;
      if (!accumulator[parentKey]) accumulator[parentKey] = [];
      accumulator[parentKey].push(item);
      return accumulator;
    }, {});
  }, [items]);

  const subtreeMatches = useMemo(() => {
    if (!normalizedSearchTerm) return null;

    const cache = {};

    const checkFolder = (folderId) => {
      if (cache[folderId] !== undefined) return cache[folderId];

      const folder = folderId === ROOT_KEY ? null : folderMap[folderId];
      const folderMatches = folder
        ? [folder.name, folder.description || ""].join(" ").toLowerCase().includes(normalizedSearchTerm)
        : false;
      const itemMatches = (itemsByParent[folderId] || []).some((item) => getItemSearchText(item).includes(normalizedSearchTerm));
      const childMatches = (childrenByParent[folderId] || []).some((childFolder) => checkFolder(String(childFolder.id)));

      cache[folderId] = folderMatches || itemMatches || childMatches;
      return cache[folderId];
    };

    checkFolder(ROOT_KEY);
    return cache;
  }, [childrenByParent, folderMap, getItemSearchText, itemsByParent, normalizedSearchTerm]);

  const toggleExpanded = (folderId) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const renderNode = (parentId = ROOT_KEY, depth = 0) => {
    const childFolders = (childrenByParent[parentId] || [])
      .filter((folder) => !subtreeMatches || subtreeMatches[String(folder.id)])
      .sort((a, b) => a.name.localeCompare(b.name));
    const childItems = (itemsByParent[parentId] || [])
      .filter((item) => !normalizedSearchTerm || getItemSearchText(item).includes(normalizedSearchTerm))
      .sort((a, b) => getItemLabel(a).localeCompare(getItemLabel(b)));

    return (
      <>
        {childFolders.map((folder) => {
          const folderId = String(folder.id);
          const hasChildren = Boolean(childrenByParent[folderId]?.length || itemsByParent[folderId]?.length);
          const isExpanded = expandedIds.has(folderId);
          const isSelected = folderId === String(currentFolderId);

          return (
            <div key={folderId} className="kb-folder-tree-node">
              <div
                className={`kb-folder-tree-row ${isSelected ? "is-selected" : ""}`}
                style={{ "--tree-depth": depth }}
              >
                <button
                  type="button"
                  className="kb-folder-tree-toggle"
                  onClick={() => (hasChildren ? toggleExpanded(folderId) : onSelectFolder(folder.id))}
                  aria-label={hasChildren ? `${isExpanded ? "Collapse" : "Expand"} ${folder.name}` : `Open ${folder.name}`}
                >
                  {hasChildren ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span />}
                </button>
                <button type="button" className="kb-folder-tree-label" onClick={() => onSelectFolder(folder.id)}>
                  <Folder size={16} />
                  <span>{folder.name}</span>
                </button>
                <div className="kb-folder-tree-actions">{renderFolderActions(folder)}</div>
              </div>
              {hasChildren && isExpanded ? renderNode(folderId, depth + 1) : null}
            </div>
          );
        })}

        {childItems.map((item) => (
          <div key={getItemId(item)} className="kb-folder-tree-node">
            <div className="kb-folder-tree-row kb-folder-tree-item-row" style={{ "--tree-depth": depth }}>
              <span className="kb-folder-tree-toggle" aria-hidden="true" />
              {onSelectItem ? (
                <button type="button" className="kb-folder-tree-label kb-folder-tree-item-label" onClick={() => onSelectItem(item)}>
                  {renderItemIcon(item)}
                  <span>{getItemLabel(item)}</span>
                </button>
              ) : (
                <div className="kb-folder-tree-label kb-folder-tree-item-label">
                  {renderItemIcon(item)}
                  <span>{getItemLabel(item)}</span>
                </div>
              )}
              <div className="kb-folder-tree-actions">{renderItemActions(item)}</div>
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <aside className="kb-folder-tree">
      <button
        type="button"
        className={`kb-folder-tree-root ${currentFolderId === ROOT_KEY ? "is-selected" : ""}`}
        onClick={() => onSelectFolder(ROOT_KEY)}
      >
        <Folder size={16} />
        <span>{rootLabel}</span>
      </button>
      <div className="kb-folder-tree-list">{renderNode()}</div>
    </aside>
  );
}

// ------------------------------------------------------------------ //
// Modals                                                               //
// ------------------------------------------------------------------ //

function ModalShell({ title, children, onClose, wide = false }) {
  return (
    <div className="kb-modal-overlay" onClick={onClose}>
      <div
        className={`kb-modal ${wide ? "kb-modal-wide" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="kb-modal-header">
          <h2>{title}</h2>
          <button type="button" className="kb-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FolderModal({ item, organizationId, selectedParentId, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    name: item?.name || "",
    description: item?.description || "",
    parent: item?.parent || (selectedParentId === ROOT_KEY ? "" : selectedParentId || ""),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        organization: organizationId,
        parent: formData.parent || null,
      };
      if (item?.id) {
        await customFetch.patch(`/api/knowledge-base-folders/${item.id}/`, payload);
      } else {
        await customFetch.post("/api/knowledge-base-folders/", payload);
      }
      onSaved();
    } catch (err) {
      setError("Could not save the folder.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell title={item?.id ? "Edit Folder" : "Create Folder"} onClose={onClose}>
      <form className="kb-modal-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
        </label>
        <label>
          Description
          <textarea
            rows="3"
            value={formData.description}
            onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
          />
        </label>
        {error ? <div className="kb-form-error">{error}</div> : null}
        <div className="kb-modal-actions">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Folder"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function KnowledgeBaseModal({ item, organizationId, selectedFolderId, onClose, onSaved }) {
  const isEdit = Boolean(item?.id);
  const [formData, setFormData] = useState({
    name: item?.name || "",
    description: item?.description || "",
    folder: item?.folder || (selectedFolderId === ROOT_KEY ? "" : selectedFolderId || ""),
    is_active: item?.is_active ?? true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        organization: organizationId,
        folder: formData.folder || null,
        is_active: formData.is_active,
      };
      if (isEdit) {
        await customFetch.patch(`/api/knowledge-bases/${item.id}/`, payload);
      } else {
        await customFetch.post("/api/knowledge-bases/", payload);
      }
      onSaved();
    } catch (err) {
      setError("Could not save the knowledge base.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell title={isEdit ? "Edit Knowledge Base" : "Create Knowledge Base"} onClose={onClose}>
      <form className="kb-modal-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
        </label>
        <label>
          Description
          <textarea
            rows="3"
            value={formData.description}
            onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
          />
        </label>
        {isEdit && (
          <div className="kb-field-info">
            <span className="kb-field-info-label">Bedrock KB Id</span>
            {item.provider_kb_id ? (
              <code className="kb-field-info-value">{item.provider_kb_id}</code>
            ) : (
              <span className="kb-field-info-empty">Not provisioned yet</span>
            )}
          </div>
        )}
        <label className="kb-checkbox">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(event) => setFormData((prev) => ({ ...prev, is_active: event.target.checked }))}
          />
          Active
        </label>
        {error ? <div className="kb-form-error">{error}</div> : null}
        <div className="kb-modal-actions">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Knowledge Base"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function SourceFolderModal({ item, knowledgeBaseId, selectedParentId, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    name: item?.name || "",
    description: item?.description || "",
    parent: item?.parent || (selectedParentId === ROOT_KEY ? "" : selectedParentId || ""),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        knowledge_base: knowledgeBaseId,
        parent: formData.parent || null,
      };
      if (item?.id) {
        await customFetch.patch(`/api/knowledge-base-source-folders/${item.id}/`, payload);
      } else {
        await customFetch.post("/api/knowledge-base-source-folders/", payload);
      }
      onSaved();
    } catch (err) {
      setError("Could not save the source folder.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell title={item?.id ? "Edit Source Folder" : "Create Source Folder"} onClose={onClose}>
      <form className="kb-modal-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
        </label>
        <label>
          Description
          <textarea
            rows="3"
            value={formData.description}
            onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
          />
        </label>
        {error ? <div className="kb-form-error">{error}</div> : null}
        <div className="kb-modal-actions">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Folder"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function SourceModal({ item, knowledgeBaseId, selectedFolderId, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    source_type: item?.source_type || "file",
    name: item?.name || "",
    description: item?.description || "",
    folder: item?.folder || (selectedFolderId === ROOT_KEY ? "" : selectedFolderId || ""),
    source_url: item?.source_url || "",
  });
  const [file, setFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    try {
      const payload = new FormData();
      payload.append("knowledge_base", knowledgeBaseId);
      payload.append("source_type", formData.source_type);
      payload.append("name", formData.name.trim());
      payload.append("description", formData.description.trim());
      if (formData.folder) {
        payload.append("folder", formData.folder);
      }
      if (formData.source_type === "url") {
        payload.append("source_url", formData.source_url.trim());
      }
      if (file) {
        payload.append("file", file);
      }

      if (item?.id) {
        await customFetch.patch(`/api/knowledge-base-sources/${item.id}/`, payload);
      } else {
        await customFetch.post("/api/knowledge-base-sources/", payload);
      }
      onSaved();
    } catch (err) {
      setError("Could not save the source.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell title={item?.id ? "Edit Source" : "Create Source"} onClose={onClose}>
      <form className="kb-modal-form" onSubmit={handleSubmit}>
        <label>
          Source Type
          <select
            value={formData.source_type}
            onChange={(event) => setFormData((prev) => ({ ...prev, source_type: event.target.value }))}
          >
            <option value="file">File</option>
            <option value="url">URL</option>
          </select>
        </label>
        <label>
          Name
          <input
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
        </label>
        <label>
          Description
          <textarea
            rows="3"
            value={formData.description}
            onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
          />
        </label>
        {formData.source_type === "url" ? (
          <label>
            URL
            <input
              type="url"
              value={formData.source_url}
              onChange={(event) => setFormData((prev) => ({ ...prev, source_url: event.target.value }))}
              required
            />
          </label>
        ) : (
          <label>
            File
            <input type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} required={!item?.id} />
          </label>
        )}
        {error ? <div className="kb-form-error">{error}</div> : null}
        <div className="kb-modal-actions">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Source"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ------------------------------------------------------------------ //
// SourceManagerModal — with per-source sync and polling               //
// ------------------------------------------------------------------ //

function SourceManagerModal({ knowledgeBase, onClose, onSaved }) {
  const [sourceFolders, setSourceFolders] = useState([]);
  const [sources, setSources] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(ROOT_KEY);
  const [folderModalItem, setFolderModalItem] = useState(null);
  const [sourceModalItem, setSourceModalItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncingSourceIds, setSyncingSourceIds] = useState(new Set());
  const pollTimerRef = useRef(null);

  const fetchData = useCallback(async () => {
    const [foldersResponse, sourcesResponse] = await Promise.all([
      customFetch.get("/api/knowledge-base-source-folders/", { params: { knowledge_base: knowledgeBase.id } }),
      customFetch.get("/api/knowledge-base-sources/", { params: { knowledge_base: knowledgeBase.id } }),
    ]);
    const updatedSources = normalizeApiResponse(sourcesResponse.data).results;
    setSourceFolders(normalizeApiResponse(foldersResponse.data).results);
    setSources(updatedSources);
    return updatedSources;
  }, [knowledgeBase.id]);

  // Poll while any source is syncing
  const schedulePoll = useCallback(() => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setTimeout(async () => {
      pollTimerRef.current = null;
      const updatedSources = await fetchData().catch(() => []);
      const stillSyncing = updatedSources.some((s) => s.sync_status === "syncing");
      if (stillSyncing) schedulePoll();
      else onSaved();
    }, POLL_INTERVAL_MS);
  }, [fetchData, onSaved]);

  useEffect(() => {
    fetchData().then((initial) => {
      if (initial.some((s) => s.sync_status === "syncing")) schedulePoll();
    });
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [fetchData, schedulePoll]);  // eslint-disable-line react-hooks/exhaustive-deps

  const deleteItem = async (kind, id) => {
    if (!window.confirm(`Delete this ${kind}?`)) return;
    const url = kind === "folder"
      ? `/api/knowledge-base-source-folders/${id}/`
      : `/api/knowledge-base-sources/${id}/`;
    await customFetch.delete(url);
    await fetchData();
    onSaved();
  };

  const syncAllSources = async () => {
    setIsSyncingAll(true);
    try {
      const response = await customFetch.post(`/api/knowledge-bases/${knowledgeBase.id}/sync/`);
      const job = response.data?.ingestion_job || {};
      window.alert(
        job.ingestion_job_id
          ? `Sync started. Ingestion job ${job.ingestion_job_id} is ${job.status || "starting"}.`
          : "Sync completed."
      );
      await fetchData();
      schedulePoll();
      onSaved();
    } catch (error) {
      const detail = error?.response?.data;
      const message = typeof detail === "string" ? detail : JSON.stringify(detail || {});
      window.alert(`Could not sync this knowledge base.\n${message}`);
    } finally {
      setIsSyncingAll(false);
    }
  };

  const syncSource = async (source) => {
    setSyncingSourceIds((prev) => new Set([...prev, String(source.id)]));
    try {
      await customFetch.post(`/api/knowledge-base-sources/${source.id}/sync/`);
      await fetchData();
      schedulePoll();
      onSaved();
    } catch (error) {
      const detail = error?.response?.data;
      const message = typeof detail === "string" ? detail : JSON.stringify(detail || {});
      window.alert(`Could not sync "${source.name}".\n${message}`);
      await fetchData();
    } finally {
      setSyncingSourceIds((prev) => {
        const next = new Set(prev);
        next.delete(String(source.id));
        return next;
      });
    }
  };

  const canSync = Boolean(knowledgeBase.provider_kb_id);
  const noSyncTitle = "Set a Provider KB Id on this knowledge base before syncing.";

  return (
    <ModalShell title={`Manage Sources: ${knowledgeBase.name}`} onClose={onClose} wide>
      <div className="kb-source-manager">
        {!canSync && (
          <div className="kb-warning-banner">
            No Provider KB Id configured — sync is disabled. Edit the knowledge base to add one.
          </div>
        )}
        <div className="kb-toolbar">
          <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search folders and sources..." />
          <div className="kb-toolbar-actions">
            <Button variant="secondary" onClick={syncAllSources} disabled={isSyncingAll || !canSync} title={!canSync ? noSyncTitle : undefined}>
              {isSyncingAll ? "Syncing..." : "Sync All"}
            </Button>
            <Button variant="secondary" leftIcon={<Folder size={16} />} onClick={() => setFolderModalItem({})}>
              New Folder
            </Button>
            <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setSourceModalItem({})}>
              New Source
            </Button>
          </div>
        </div>

        <HierarchyTree
          folders={sourceFolders}
          items={sources}
          currentFolderId={currentFolderId}
          rootLabel={knowledgeBase.name}
          searchTerm={searchTerm}
          onSelectFolder={setCurrentFolderId}
          getItemId={(item) => `source-${item.id}`}
          getItemLabel={(item) => item.name}
          getItemSearchText={(item) =>
            [item.name, item.description || "", item.source_url || "", item.file_url || ""].join(" ").toLowerCase()
          }
          renderItemIcon={(item) => (item.source_type === "url" ? <LinkIcon size={16} /> : <FileText size={16} />)}
          renderFolderActions={(folder) => (
            <>
              <button type="button" onClick={() => setFolderModalItem(folder)}>Edit</button>
              <button type="button" onClick={() => deleteItem("folder", folder.id)}>Delete</button>
            </>
          )}
          renderItemActions={(item) => {
            const isSyncing = syncingSourceIds.has(String(item.id)) || item.sync_status === "syncing";
            const syncDisabled = isSyncing || !canSync;
            const syncTitle = !canSync ? noSyncTitle : item.sync_error || undefined;
            return (
              <>
                <SyncBadge status={item.sync_status} />
                <button
                  type="button"
                  onClick={() => syncSource(item)}
                  disabled={syncDisabled}
                  title={syncTitle}
                >
                  {isSyncing ? "Syncing..." : "Sync"}
                </button>
                <button type="button" onClick={() => setSourceModalItem(item)}>Edit</button>
                <button type="button" onClick={() => deleteItem("source", item.id)}>Delete</button>
              </>
            );
          }}
        />
      </div>

      {folderModalItem !== null ? (
        <SourceFolderModal
          item={folderModalItem.id ? folderModalItem : null}
          knowledgeBaseId={knowledgeBase.id}
          selectedParentId={currentFolderId}
          onClose={() => setFolderModalItem(null)}
          onSaved={async () => {
            setFolderModalItem(null);
            await fetchData();
            onSaved();
          }}
        />
      ) : null}

      {sourceModalItem !== null ? (
        <SourceModal
          item={sourceModalItem.id ? sourceModalItem : null}
          knowledgeBaseId={knowledgeBase.id}
          selectedFolderId={currentFolderId}
          onClose={() => setSourceModalItem(null)}
          onSaved={async () => {
            setSourceModalItem(null);
            await fetchData();
            onSaved();
          }}
        />
      ) : null}
    </ModalShell>
  );
}

// ------------------------------------------------------------------ //
// Main KnowledgeBases page                                            //
// ------------------------------------------------------------------ //

export default function KnowledgeBases() {
  const { user } = useUser();
  const [folders, setFolders] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(ROOT_KEY);
  const [searchTerm, setSearchTerm] = useState("");
  const [folderModalItem, setFolderModalItem] = useState(null);
  const [knowledgeBaseModalItem, setKnowledgeBaseModalItem] = useState(null);
  const [sourceManagerItem, setSourceManagerItem] = useState(null);
  const [syncingKnowledgeBaseId, setSyncingKnowledgeBaseId] = useState(null);
  const pollTimerRef = useRef(null);

  const refreshData = useCallback(async () => {
    const [foldersResponse, knowledgeBasesResponse] = await Promise.all([
      customFetch.get("/api/knowledge-base-folders/"),
      customFetch.get("/api/knowledge-bases/"),
    ]);
    const updatedKBs = normalizeApiResponse(knowledgeBasesResponse.data).results;
    setFolders(normalizeApiResponse(foldersResponse.data).results);
    setKnowledgeBases(updatedKBs);
    return updatedKBs;
  }, []);

  // Poll while any KB has sources syncing
  const schedulePool = useCallback(() => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setTimeout(async () => {
      pollTimerRef.current = null;
      const updatedKBs = await refreshData().catch(() => []);
      const stillSyncing = updatedKBs.some((kb) => kb.sync_status === "syncing");
      if (stillSyncing) schedulePool();
    }, POLL_INTERVAL_MS);
  }, [refreshData]);

  useEffect(() => {
    refreshData().then((initial) => {
      if (initial.some((kb) => kb.sync_status === "syncing")) schedulePool();
    });
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const organizationId = useMemo(() => {
    const userOrganization = user?.organization?.id || user?.organization || user?.organization_id?.id || user?.organization_id;
    const firstFolderOrg = folders[0]?.organization;
    const firstKnowledgeBaseOrg = knowledgeBases[0]?.organization;
    return userOrganization || firstFolderOrg || firstKnowledgeBaseOrg || null;
  }, [folders, knowledgeBases, user]);

  const deleteItem = async (kind, id) => {
    if (!window.confirm(`Delete this ${kind}?`)) return;
    const url = kind === "folder" ? `/api/knowledge-base-folders/${id}/` : `/api/knowledge-bases/${id}/`;
    await customFetch.delete(url);
    await refreshData();
  };

  const syncKnowledgeBase = async (knowledgeBase) => {
    setSyncingKnowledgeBaseId(String(knowledgeBase.id));
    try {
      const response = await customFetch.post(`/api/knowledge-bases/${knowledgeBase.id}/sync/`);
      const job = response.data?.ingestion_job || {};
      window.alert(
        job.ingestion_job_id
          ? `Sync started for ${knowledgeBase.name}. Ingestion job ${job.ingestion_job_id} is ${job.status || "starting"}.`
          : `Sync completed for ${knowledgeBase.name}.`
      );
      await refreshData();
      schedulePool();
    } catch (err) {
      const detail = err?.response?.data;
      const message = typeof detail === "string" ? detail : JSON.stringify(detail || {});
      window.alert(`Could not sync ${knowledgeBase.name}.\n${message}`);
    } finally {
      setSyncingKnowledgeBaseId(null);
    }
  };

  return (
    <div className="knowledge-bases-root">
      <div className="knowledge-bases-header">
        <div>
          <h1 className="knowledge-bases-title">Knowledge Bases</h1>
          <p className="knowledge-bases-subtitle">
            Create organization knowledge bases, organize them in folders, and manage file or URL sources for each one.
          </p>
        </div>
        <div className="knowledge-bases-header-actions">
          <Button variant="secondary" leftIcon={<Folder size={16} />} onClick={() => setFolderModalItem({})}>
            New Folder
          </Button>
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setKnowledgeBaseModalItem({})}>
            New Knowledge Base
          </Button>
        </div>
      </div>

      <div className="knowledge-bases-stats">
        <div className="knowledge-bases-stat-card">
          <BookOpen size={18} />
          <div>
            <strong>{knowledgeBases.length}</strong>
            <span>Total KBs</span>
          </div>
        </div>
        <div className="knowledge-bases-stat-card">
          <Folder size={18} />
          <div>
            <strong>{folders.length}</strong>
            <span>Total folders</span>
          </div>
        </div>
        <div className="knowledge-bases-stat-card">
          <Settings size={18} />
          <div>
            <strong>{knowledgeBases.filter((item) => item.is_active).length}</strong>
            <span>Active KBs</span>
          </div>
        </div>
      </div>

      <div className="kb-toolbar">
        <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search folders and knowledge bases..." />
      </div>

      <HierarchyTree
        folders={folders}
        items={knowledgeBases}
        currentFolderId={currentFolderId}
        rootLabel="Root"
        searchTerm={searchTerm}
        onSelectFolder={setCurrentFolderId}
        onSelectItem={setSourceManagerItem}
        getItemId={(item) => `kb-${item.id}`}
        getItemLabel={(item) => item.name}
        getItemSearchText={(item) =>
          [item.name, item.description || "", `${item.roles_count || 0} roles`, `${item.sources_count || 0} sources`]
            .join(" ")
            .toLowerCase()
        }
        renderItemIcon={() => <BookOpen size={16} />}
        renderFolderActions={(folder) => (
          <>
            <button type="button" onClick={() => setFolderModalItem(folder)}>Edit</button>
            <button type="button" onClick={() => deleteItem("folder", folder.id)}>Delete</button>
          </>
        )}
        renderItemActions={(item) => {
          const isSyncing = syncingKnowledgeBaseId === String(item.id);
          const hasProviderKbId = Boolean(item.provider_kb_id);
          return (
          <>
            <SyncBadge status={item.sync_status} />
            <button
              type="button"
              onClick={() => syncKnowledgeBase(item)}
              disabled={isSyncing || !hasProviderKbId}
              title={!hasProviderKbId ? "Set a Provider KB Id before syncing." : undefined}
            >
              {isSyncing ? "Syncing..." : "Sync"}
            </button>
            <button type="button" onClick={() => setSourceManagerItem(item)}>Sources</button>
            <button type="button" onClick={() => setKnowledgeBaseModalItem(item)}>Edit</button>
            <button type="button" onClick={() => deleteItem("knowledge base", item.id)}>
              <Trash2 size={14} />
            </button>
          </>
          );
        }}
      />

      {folderModalItem !== null ? (
        <FolderModal
          item={folderModalItem.id ? folderModalItem : null}
          organizationId={organizationId}
          selectedParentId={currentFolderId}
          onClose={() => setFolderModalItem(null)}
          onSaved={async () => {
            setFolderModalItem(null);
            await refreshData();
          }}
        />
      ) : null}

      {knowledgeBaseModalItem !== null ? (
        <KnowledgeBaseModal
          item={knowledgeBaseModalItem.id ? knowledgeBaseModalItem : null}
          organizationId={organizationId}
          selectedFolderId={currentFolderId}
          onClose={() => setKnowledgeBaseModalItem(null)}
          onSaved={async () => {
            setKnowledgeBaseModalItem(null);
            await refreshData();
          }}
        />
      ) : null}

      {sourceManagerItem ? (
        <SourceManagerModal
          knowledgeBase={sourceManagerItem}
          onClose={() => setSourceManagerItem(null)}
          onSaved={refreshData}
        />
      ) : null}
    </div>
  );
}
